/**
 * webPromotionService.js
 * Handles all communication with the website's POS-facing promotion endpoints.
 * The website URL is configured via VITE_WEBSITE_URL environment variable.
 */

const WEBSITE_BASE = import.meta.env.VITE_WEBSITE_URL || 'https://tezlaa.com';
const POS_API_KEY  = import.meta.env.VITE_POS_API_KEY  || '';

const headers = () => ({
  'Content-Type': 'application/json',
  'x-api-key': POS_API_KEY,
});

/**
 * Fetch all active web promotions for a customer by mobile number.
 * @param {string} mobile
 * @returns {Promise<Array>} promotions array
 */
const fetchCustomerWebPromotions = async (mobile) => {
  if (!mobile || mobile === '0000000000') return [];

  try {
    const res = await fetch(`${WEBSITE_BASE}/api/pos/customer-promotions`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ mobile }),
    });

    if (!res.ok) {
      console.warn('[webPromotionService] fetchCustomerWebPromotions failed:', res.status);
      return [];
    }

    const data = await res.json();
    return data?.promotions || [];
  } catch (err) {
    console.error('[webPromotionService] fetchCustomerWebPromotions error:', err);
    return [];
  }
};

/**
 * Claim a specific web promotion for a customer.
 * @param {string} mobile
 * @param {string} webCustomerPromotionId
 * @returns {Promise<{success: boolean, promotion?: object, error?: string}>}
 */
const claimWebPromotion = async (mobile, webCustomerPromotionId) => {
  try {
    const res = await fetch(`${WEBSITE_BASE}/api/pos/claim-promotion`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ mobile, webCustomerPromotionId }),
    });

    const data = await res.json();

    if (!res.ok) {
      return { success: false, error: data?.error || 'Failed to claim promotion' };
    }

    return { success: true, promotion: data.promotion };
  } catch (err) {
    console.error('[webPromotionService] claimWebPromotion error:', err);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Send an OTP to the customer's mobile for promotion verification.
 * @param {string} mobile
 * @returns {Promise<{success: boolean, otpId?: string, smsSent?: boolean, error?: string}>}
 */
const sendPromoOtp = async (mobile) => {
  try {
    const res = await fetch(`${WEBSITE_BASE}/api/pos/send-otp`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ mobile }),
    });

    const data = await res.json();

    if (!res.ok) {
      return { success: false, error: data?.error || 'Failed to send OTP' };
    }

    console.log('[webPromotionService] OTP sent:', data);
    return { success: true, otpId: data.otpId, smsSent: data.smsSent };
  } catch (err) {
    console.error('[webPromotionService] sendPromoOtp error:', err);
    return { success: false, error: 'Network error sending OTP' };
  }
};

/**
 * Verify the OTP a customer provides for promotion verification.
 * @param {string} otpId - OTP record ID returned by sendPromoOtp
 * @param {string} code  - 6-digit code entered by cashier
 * @returns {Promise<{success: boolean, error?: string}>}
 */
const verifyPromoOtp = async (otpId, code) => {
  try {
    const res = await fetch(`${WEBSITE_BASE}/api/pos/verify-otp`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ otpId, code }),
    });

    const data = await res.json();

    if (!res.ok) {
      return { success: false, error: data?.error || 'OTP verification failed' };
    }

    return { success: true };
  } catch (err) {
    console.error('[webPromotionService] verifyPromoOtp error:', err);
    return { success: false, error: 'Network error verifying OTP' };
  }
};

const webPromotionService = {
  fetchCustomerWebPromotions,
  claimWebPromotion,
  sendPromoOtp,
  verifyPromoOtp,
};

export default webPromotionService;
